import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from '@posthog/react';
/* MessageCircle (nem Sparkles) a lebegő chat-indítón — Gábor döntése 2026-09-10:
 * a chat ikon a weben és a mobilon EGYSÉGES legyen, és kör alakú
 * (mobil redesign 3. kör). A `Sparkles` máshol (Documents.js AI-művelet) marad. */
import { MessageCircle, Send, Minus, FileText, Check } from 'lucide-react';
import { Spinner } from './UI';
import { askChatStream } from '../services/chat';

/* Dashboard AI chat — a puo AI asszisztense (D-WP-06).
 *
 * ⚠ NEM modális: nincs sötét háttér-elhalványítás, a felület mögötte
 * kattintható marad. Szándékos eltérés a `FeedbackWidget`/`Modal` mintától —
 * a chat célja pont az, hogy a felhasználó a válasz mellett a felületen is
 * dolgozzon (az `open_payment_modal`-féle UI-intentek, `Q-D2`/D-WP-10/11,
 * egy modálban lévő modálban csúnya lenne — a fizetési modal a `Documents.js`
 * saját, teljes-képernyős felületén nyílik, a chat-panel pedig zár is
 * mellette, lásd `handleUiIntent`).
 *
 * ⚠ A beszélgetés-előzmény CSAK ennek a React-komponensnek az élettartamáig él
 * (Layout nem szereli le route-váltáskor, tehát egy SPA-munkamenetig kitart,
 * de oldal-újratöltésre elvész) — a backend a saját oldalán (`chat_conversation_turns`)
 * ennél tartósabban emlékszik, és ez adja a folytatólagos kontextust, ha a
 * panel újranyílik. Nincs "előzmény betöltése" végpont ehhez a körhöz (D-WP-06
 * hatóköre), a kliens-oldali lista tehát minden újratöltés után üresen indul.
 */

const DISCLOSURE_TEXT = 'Hello! A puo AI chat asszisztense vagyok. Miben segíthetek?'; // Q-D5

function newId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/* `done=false` amíg a kör folyamatban van — a Spinner csak eddig pörög.
 * Korábban a jelzés-sor MINDIG a Spinnert mutatta, a `done` streamelt esemény
 * után is, mert semmi nem kötötte a spinnert a kör állapotához — élő hiba,
 * Gábor jelentette (2026-08-25): a válasz megérkezése után a korábbi
 * tool-jelzések tovább pörögtek. */
function ToolSignalLine({ text, done }) {
  return (
    <div style={{display:'flex', alignItems:'center', gap:6, fontSize:'0.74rem',
      color:'var(--text-muted)', fontStyle:'italic', padding:'0.1rem 0'}}>
      {done ? <Check size={11}/> : <Spinner size={11}/>} {text}
    </div>
  );
}

/* D-WP-07: az itt renderelt referenciák (`msg.references`) KIZÁRÓLAG a
 * backend tool-eredményéből származó `document_id`-k — soha a modell
 * szövegéből kinyert link. A `/documents/:docId` a route, ami már ma is
 * mélylinkelhető (`App.js`), ezért nem kellett új útvonal. */
function ReferenceChip({ reference, onClick }) {
  return (
    <button onClick={() => onClick(reference)} title="Bizonylat megnyitása"
      style={{display:'inline-flex', alignItems:'center', gap:4, fontSize:'0.72rem',
        padding:'0.28rem 0.6rem', borderRadius:999, border:'1px solid var(--border)',
        background:'var(--bg-input)', color:'var(--primary)', cursor:'pointer',
        maxWidth:180, overflow:'hidden'}}>
      <FileText size={11} style={{flexShrink:0}}/>
      <span style={{overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
        {reference.label}
      </span>
    </button>
  );
}

function MessageBubble({ msg, onReferenceClick }) {
  const isUser = msg.role === 'user';
  const isError = msg.role === 'error';
  return (
    <div style={{display:'flex', flexDirection:'column', alignItems: isUser ? 'flex-end' : 'flex-start'}}>
      {!isUser && msg.toolSignals?.length > 0 && (
        <div style={{marginBottom:4, maxWidth:'88%'}}>
          {msg.toolSignals.map((s, i) => <ToolSignalLine key={i} text={s} done={!msg.streaming}/>)}
        </div>
      )}
      <div style={{
        maxWidth:'88%', padding:'0.5rem 0.7rem', borderRadius:'var(--radius-lg)',
        fontSize:'0.82rem', lineHeight:1.5, whiteSpace:'pre-wrap', wordBreak:'break-word',
        background: isUser ? 'var(--brand, var(--primary))'
                  : isError ? 'var(--danger-subtle)' : 'var(--bg-hover)',
        color: isUser ? 'var(--brand-contrast, #fff)' : isError ? 'var(--danger)' : 'var(--text)',
        border: isError ? '1px solid var(--danger)' : 'none',
      }}>
        {msg.text || (msg.streaming ? '…' : '')}
      </div>
      {!isUser && msg.references?.length > 0 && (
        <div style={{display:'flex', flexWrap:'wrap', alignItems:'center', gap:5, marginTop:5, maxWidth:'88%'}}>
          {/* Védőháló a szűk panelhez, függetlenül a backend limit-jétől
              (`chat_query.list_unpaid_invoices` stb. ma 50-nél vág) —
              lásd a D-WP-07 élő hibáját (841 chip egy 380px-es panelen). */}
          {msg.references.slice(0, 8).map((r) => (
            <ReferenceChip key={`${r.type}-${r.id}`} reference={r} onClick={onReferenceClick}/>
          ))}
          {msg.references.length > 8 && (
            <span style={{fontSize:'0.7rem', color:'var(--text-dim)'}}>
              +{msg.references.length - 8}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export default function ChatWidget() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => (
    [{ id: 'disclosure', role: 'assistant', text: DISCLOSURE_TEXT }]
  ));
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  function handleReferenceClick(reference) {
    if (reference.type === 'document') {
      navigate(`/documents/${reference.id}`);
      setOpen(false);
    }
  }

  /* D-WP-11 — a ZÁRT UI-intent kapcsoló. Egy ISMERETLEN `event.intent` ELDOBVA
   * és logolva, SOHA nem értelmezve (dashboard-chat.md saját szabálya — a
   * modell szövegéből ide semmi nem kerülhet, csak a backend által már
   * validált, zárt eseménytípus). Ma a backend `open_document`-et ÉS
   * `open_payment_modal`-t küldi (D-WP-10); a maradék négy nevet azért
   * ismeri a `case`, hogy a kapcsoló KÉSZ maradjon egy jövőbeli körre — de
   * nem hívnak semmilyen kezelőt, mert a backend ma nem emitálja őket. */
  function handleUiIntent(event) {
    switch (event.intent) {
      case 'open_document':
        navigate(`/documents/${event.params.document_id}`);
        setOpen(false);
        break;
      case 'open_payment_modal':
        // A `Documents.js`-en a `pay` query-param nyitja meg a modalt
        // előre kitöltve — lásd az ottani `useEffect`-et (D-WP-10). A tényleges
        // utalást a MEGLÉVŐ modal indítja, a felhasználó kattintására.
        navigate(`/documents?pay=${event.params.document_ids.join(',')}`);
        setOpen(false);
        break;
      case 'open_filtered_list':
      case 'open_category_rule':
      case 'open_project_allocation':
      case 'open_accountant_package':
      case 'open_settings':
        // eslint-disable-next-line no-console
        console.warn('UI intent ismert, de kezelő még nincs bekötve:', event.intent);
        break;
      default:
        // eslint-disable-next-line no-console
        console.warn('Ismeretlen UI intent — eldobva:', event.intent);
    }
  }

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, open]);

  useEffect(() => () => abortRef.current?.abort(), []);

  async function send() {
    const question = input.trim();
    if (!question || busy) return;
    setInput('');
    setBusy(true);
    const userMsg = { id: newId(), role: 'user', text: question };
    const answerId = newId();
    setMessages((m) => [...m, userMsg,
      { id: answerId, role: 'assistant', text: '', streaming: true, toolSignals: [], references: [] }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      await askChatStream(question, {
        signal: controller.signal,
        onEvent: (ev) => {
          if (ev.type === 'meta') {
            setQuota(ev.quota);
          } else if (ev.type === 'tool_signal') {
            setMessages((m) => m.map((x) => x.id === answerId
              ? { ...x, toolSignals: [...x.toolSignals, ev.text] } : x));
          } else if (ev.type === 'ui_intent') {
            handleUiIntent(ev);
          } else if (ev.type === 'token') {
            setMessages((m) => m.map((x) => x.id === answerId
              ? { ...x, text: x.text + ev.text } : x));
          } else if (ev.type === 'error') {
            setMessages((m) => m.map((x) => x.id === answerId
              ? { ...x, role: 'error', text: 'Hiba történt a válasz közben. Próbáld újra.', streaming: false } : x));
            setBusy(false);
          } else if (ev.type === 'done') {
            setMessages((m) => m.map((x) => x.id === answerId
              ? { ...x, text: ev.answer, streaming: false, references: ev.references || [] } : x));
            // ⚠⚠ Élő hiba, Gábor jelentette (2026-08-25): a `busy`-t (és vele a
            // küldés-gomb Spinnerét) korábban CSAK a `finally` ág oldotta fel,
            // ami a `fetch` STREAM tényleges lezárására várt — a backend viszont
            // a `done` esemény KIKÜLDÉSE UTÁN még ír (kvóta, model-hívás napló,
            // PostHog), így a spinner a válasz teljes megjelenése UTÁN is
            // pörgött még egy darabig. A `done` a beszélgetés szempontjából már
            // KÉSZ — itt oldjuk fel azonnal, a `finally` csak védőháló marad
            // (AbortError/hálózati hiba esetére, ahol `done` sosem érkezik).
            setBusy(false);
          }
        },
      });
    } catch (err) {
      if (err.name === 'AbortError') {
        // a panel bezárása / unmount szándékosan szakította meg — nincs hibaüzenet
      } else if (err.status === 429) {
        setMessages((m) => m.map((x) => x.id === answerId
          ? { ...x, role: 'error', text: err.message, streaming: false } : x));
      } else {
        setMessages((m) => m.map((x) => x.id === answerId
          ? { ...x, role: 'error', text: 'Nem sikerült elérni az asszisztenst. Próbáld újra.', streaming: false } : x));
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  }

  if (!open) {
    return (
      <button onClick={() => { setOpen(true); posthog?.capture('chat_opened'); }}
        title="AI asszisztens" aria-label="Chat megnyitása"
        style={{
          position:'fixed', right:'1rem', bottom:'4.5rem', zIndex:60,
          display:'flex', alignItems:'center', justifyContent:'center',
          width:44, height:44, borderRadius:'50%', border:'none', cursor:'pointer',
          background:'linear-gradient(135deg,#109D7B,#009C96,#0097B3)',
          color:'#fff', boxShadow:'var(--shadow)',
        }}>
        <MessageCircle size={20}/>
      </button>
    );
  }

  return (
    <div role="dialog" aria-modal="false" aria-label="puo AI asszisztens"
      style={{
        position:'fixed', right:'1rem', bottom:'1rem', zIndex:60,
        width:'min(380px, calc(100vw - 2rem))', height:'min(560px, calc(100vh - 2rem))',
        background:'var(--bg-surface)', border:'1px solid var(--border)',
        borderRadius:'var(--radius-lg)', boxShadow:'var(--shadow)',
        display:'flex', flexDirection:'column', overflow:'hidden',
      }}>
      <header style={{display:'flex', alignItems:'center', gap:8, padding:'0.7rem 0.9rem',
        borderBottom:'1px solid var(--border)', flexShrink:0}}>
        <span style={{fontFamily:'var(--font-logo)', fontStyle:'italic', fontWeight:800,
          fontSize:16, background:'linear-gradient(90deg,#109D7B,#009C96,#0097B3)',
          WebkitBackgroundClip:'text', backgroundClip:'text', color:'transparent'}}>
          puo
        </span>
        <b style={{fontSize:'0.82rem'}}>AI asszisztens</b>
        {quota && (
          <span title="Napi kérdés-kvóta" style={{marginLeft:'auto', fontSize:'0.66rem',
            color:'var(--text-dim)'}}>
            {quota.used}/{quota.limit}
          </span>
        )}
        {/* ⚠ NEM zárja be a beszélgetést — csak elrejti a panelt, a `messages`
         * state a Layout-tal együtt tovább él (lásd a fájl tetején lévő
         * megjegyzést). Korábban `X` ikon + "Bezárás" felirat volt, ami
         * hamisan azt sugallta, hogy a beszélgetés elvész — Gábor jelezte
         * (2026-08-25), hogy egy kis méretre állító ikon egyértelműbb lenne. */}
        <button onClick={() => setOpen(false)} aria-label="Kis méretre állítás"
          title="Kis méretre állítás — a beszélgetés megmarad"
          style={{marginLeft: quota ? 8 : 'auto', background:'none', border:'none',
            color:'var(--text-muted)', cursor:'pointer', display:'flex'}}>
          <Minus size={16}/>
        </button>
      </header>

      <div ref={listRef} style={{flex:1, overflowY:'auto', padding:'0.8rem',
        display:'flex', flexDirection:'column', gap:'0.6rem'}}>
        {messages.map((m) => <MessageBubble key={m.id} msg={m} onReferenceClick={handleReferenceClick}/>)}
      </div>

      <div style={{display:'flex', gap:6, padding:'0.6rem', borderTop:'1px solid var(--border)', flexShrink:0}}>
        <textarea rows={1} value={input} disabled={busy}
          onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
          placeholder="Kérdezz a pénzügyeidről…"
          style={{flex:1, resize:'none', background:'var(--bg-input)', color:'var(--text)',
            border:'1px solid var(--border)', borderRadius:'var(--radius)',
            padding:'0.45rem 0.6rem', fontSize:'0.82rem', fontFamily:'inherit',
            maxHeight:80}}/>
        <button onClick={send} disabled={busy || !input.trim()} aria-label="Küldés"
          style={{width:36, height:36, flexShrink:0, borderRadius:'var(--radius)', border:'none',
            cursor: (busy || !input.trim()) ? 'not-allowed' : 'pointer',
            opacity: (busy || !input.trim()) ? 0.5 : 1,
            background:'var(--brand, var(--primary))', color:'var(--brand-contrast, #fff)',
            display:'flex', alignItems:'center', justifyContent:'center'}}>
          {busy ? <Spinner size={14}/> : <Send size={15}/>}
        </button>
      </div>
    </div>
  );
}
